import { isIOS } from '@fi-shared/utils/isIOS';
import { AppTextService } from '@fi-shared/app-text';
import { findClosest, isDefined, isString } from '@fi-shared/utils';
import { Injectable } from '@angular/core';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { ISelectedButtonsData } from '@fi-shared/fi-text-editor/fi-text-editor';

// check the 'com.apple.webarchive' for a case of working in safari. 'text/html' is empty there when copy from the page
const CD_TYPES = ['text/html', 'text/rtf', 'public.html', 'public.rtf', 'com.apple.webarchive'];

interface IOnPasteArgs {
    onImagesPasteCallback?: (files: File[]) => void;
    isSnackBarShown?: boolean;
    disableFormatting?: boolean;
}

function clipboardDataGetContent(clipboardData: DataTransfer): string {
    // first check primary known types, then check all string types
    return (
        clipboardDataGetContentByTypes(clipboardData, CD_TYPES) ||
        clipboardDataGetContentByTypes(
            clipboardData,
            clipboardData.types.filter((type) => type !== 'text/plain'),
        ) ||
        ''
    );
}

function clipboardDataGetContentByTypes(clipboardData: DataTransfer, types): string {
    for (let i = 0; i < types.length; i++) {
        const data = clipboardData.getData(types[i]);
        if (data && isString(data)) {
            return data;
        }
    }

    return '';
}

@Injectable()
export class FiContenteditableService {
    constructor(
        private appTextService: AppTextService,
        private snackBar: FiSnackBarService,
    ) {}

    getRangeStartElement(): HTMLElement {
        const selection: Selection = window.getSelection();
        if (selection.rangeCount) {
            const range = selection.getRangeAt(0);
            return range.startContainer.nodeType === Node.TEXT_NODE
                ? range.startContainer.parentElement
                : <any>range.startContainer;
        }
    }

    /* sets cursor position to start/end of content editable element
     */
    setCursorPosition(element: HTMLElement, toStart?: boolean) {
        let range: Range, selection: Selection;
        if (document.createRange) {
            range = document.createRange(); //Create a range (a range is a like the selection but invisible)
            range.selectNodeContents(element); //Select the entire contents of the element with the range
            range.collapse(toStart); //collapse the range to the end point. false means collapse to end rather than the start
            selection = window.getSelection(); //get the selection object (allows you to change selection)
            selection.removeAllRanges(); //remove any selections already made
            selection.addRange(range); //make the range you have just created the visible selection
        }
    }

    buffer: string;

    onCopy($event: ClipboardEvent) {
        const bufferElement = document.createElement('DIV');
        bufferElement.appendChild(window.getSelection().getRangeAt(0).cloneContents().cloneNode(true));
        this.buffer = bufferElement.innerHTML;
        $event.clipboardData.setData('text/html', this.buffer);
        $event.clipboardData.setData('text/plain', bufferElement.textContent);
        $event.preventDefault();
    }

    onPaste($event: ClipboardEvent, args: IOnPasteArgs) {
        if (!isDefined(args.isSnackBarShown)) {
            args.isSnackBarShown = true;
        }
        if (!isDefined(args.disableFormatting)) {
            args.disableFormatting = false;
        }
        let items = $event.clipboardData.items;
        if (items) {
            let imageItems = Array.from(items).filter((item) => item.type.indexOf('image') !== -1);
            if (imageItems.length) {
                $event.preventDefault();
                if (args.onImagesPasteCallback) {
                    args.onImagesPasteCallback(imageItems.map((imageItem) => imageItem.getAsFile()).filter((f) => f));
                }
                return Promise.resolve();
            }
        }

        return new Promise<void>((resolve, reject) => {
            const clipboardData = $event.clipboardData;

            let content = clipboardDataGetContent(clipboardData);

            if (content) {
                // check if pasted content is copied from inside a ccm contenteditable block
                // is is allowed to paste formatted html in this case
                const contentBody = content
                    ? content.replace(/^(.|\n|\r)+StartFragment-->/gm, '').replace(/<!--EndFragment(.|\n|\r)+$/gm, '')
                    : '';
                let bufferBody = '';
                if (this.buffer) {
                    bufferBody = this.buffer
                        .replace(/^(.|\n|\r)+StartFragment-->/gm, '')
                        .replace(/<!--EndFragment(.|\n|\r)+$/gm, '');
                }

                if (contentBody != bufferBody || args.disableFormatting) {
                    $event.preventDefault();
                    content = clipboardData.getData('text/plain');

                    const newNode = document.createElement('span');
                    //newNode.innerHTML = content.replace(/\n/g, "</br>");
                    newNode.innerText = content;

                    const sel = window.getSelection();
                    const range = sel.getRangeAt(0);

                    range.deleteContents();
                    range.insertNode(newNode);
                    //range.setStart(newNode, newNode.childNodes.length);
                    sel.removeAllRanges();
                    sel.addRange(range);
                    range.collapse();
                    newNode.scrollIntoView({
                        block: 'end',
                    });
                    resolve();
                    if (args.isSnackBarShown)
                        this.snackBar.showSnackbar({
                            textMessage: this.appTextService.get('common.formatting_was_removed'),
                            buttonText: this.appTextService.get('common.dismiss'),
                        });

                    /*this.fiDialogsService.confirm({
                        headerText: this.appTextService.get('text_editor.paste_formatted_text'),
                        messageText: this.appTextService.get('text_editor.text_contains_formatting_warning'),
                    }).onYesButtonClick(() => {
                            range.deleteContents();
                            range.insertNode(newNode);
                            sel.removeAllRanges();
                            sel.addRange(range);
                            resolve();
                        }).onNoButtonClick(() => resolve());*/
                } else {
                    if (isIOS()) {
                        $event.preventDefault();
                        content = clipboardData.getData('text/plain');
                        document.execCommand('insertText', false, content);
                    }
                    resolve();
                }
            } else {
                resolve();
            }
        });
    }

    updateSelectedButtonsData(
        selectedButtonsData: ISelectedButtonsData,
        el: HTMLElement,
        isAdvancedPanel: boolean,
        containerClass: string,
    ) {
        selectedButtonsData.bold = !!findClosest(el, 'b', containerClass);
        selectedButtonsData.underline = !!findClosest(el, 'u', containerClass);
        selectedButtonsData.italic = !!findClosest(el, 'i', containerClass);
        if (isAdvancedPanel) {
            selectedButtonsData.strikeThrough = !!findClosest(el, 'strike', containerClass);
            selectedButtonsData.superscript = !!findClosest(el, 'sup', containerClass);
            selectedButtonsData.subscript = !!findClosest(el, 'sub', containerClass);
        }
    }

    placeCaretAtEnd(element: HTMLElement) {
        // https://stackoverflow.com/questions/4233265/contenteditable-set-caret-at-the-end-of-the-text-cross-browser
        const range = document.createRange();
        range.selectNodeContents(element);
        range.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
    }
}
